import React from "react";
import classnames from "classnames";
import Select from "react-select";
import { Link } from "react-router";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  Container,
  Col,
  Label,
  FormGroup,
  InputGroup,
  InputGroupText,
  Input,
  Row,
} from "reactstrap";
import SimpleHeader from "@/components/Headers/SimpleHeader";
import { ToastContainer } from "react-toastify";
import ImageUploadContainer, {
  type PickedImage,
} from "../User/adding-user/ImageUploadContainer";
import type { SelectOption } from "@/utils/selectOptions";

// The form state of AddNewClientContainer.tsx; `deletedHolder` is the
// soft-deleted client a 409 named (SCRUM-1818), of which this view shows the
// message and the name.
export type AddNewClientViewProps = {
  client: {
    name: string;
    about: string;
    contacts: { email: string; number: string; website: string };
    social_links: { facebook: string; linkedin: string; twitter: string };
    adresse: string;
    country_code: string;
    legal_name: string;
  };
  validation: { name: boolean; country_code: boolean };
  updatedCountries: readonly SelectOption[];
  loading: boolean;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  onChangeCountryCode: (option: SelectOption | null) => void;
  onClickAdd: React.MouseEventHandler<HTMLButtonElement>;
  ImageUploadRecievedHandler: (img: PickedImage) => void;
  deletedHolder: { msg: string; value: string } | null;
  onRestore: () => void;
  onStartFresh: () => void;
};

const AddNewClientView = ({
  client,
  validation,
  updatedCountries,
  loading,
  onChange,
  onChangeCountryCode,
  onClickAdd,
  ImageUploadRecievedHandler,
  deletedHolder,
  onRestore,
  onStartFresh,
}: AddNewClientViewProps) => {
  return (
    <>
      {/* NOTIFICATION DIV */}
      <div className="rna-wrapper">
        <ToastContainer />
      </div>

      <SimpleHeader name="Add New Client" parentName="Clients" />

      <Container className="mt--6" fluid>
        <Row>
          <Col className="order-xl-1" xl="8">
            <Card>
              <CardHeader>
                <Row className="align-items-center">
                  <Col xs="8">
                    <h3 className="mb-0">Add New Client</h3>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                <h6 className="heading-small text-muted mb-4">
                  General information
                </h6>

                {/* Name */}
                <FormGroup className="row">
                  <Label className="form-control-label" md="2">
                    Name
                  </Label>
                  <Col md="7">
                    <InputGroup className={classnames("input-group-merge")}>
                      <Input
                        placeholder="Name"
                        name="name"
                        type="text"
                        value={client.name}
                        onChange={onChange}
                        invalid={!validation.name}
                      />
                    </InputGroup>
                    {!validation.name ? (
                      <InputGroup className="invalid-feedback">
                        Required Name.
                      </InputGroup>
                    ) : (
                      ""
                    )}
                  </Col>
                </FormGroup>

                {/* Legal Name */}
                <FormGroup className="row">
                  <Label className="form-control-label" md="2">
                    Legal Name
                  </Label>
                  <Col md="7">
                    <InputGroup className={classnames("input-group-merge")}>
                      <Input
                        placeholder="Legal Name"
                        name="legal_name"
                        type="text"
                        value={client.legal_name}
                        onChange={onChange}
                      />
                    </InputGroup>
                  </Col>
                </FormGroup>

                {/* About */}
                <FormGroup className="row">
                  <Label className="form-control-label" md="2">
                    About
                  </Label>
                  <Col md="9">
                    <InputGroup className={classnames("input-group-merge")}>
                      <Input
                        placeholder="About the company .."
                        name="about"
                        type="textarea"
                        value={client.about}
                        onChange={onChange}
                      />
                    </InputGroup>
                  </Col>
                </FormGroup>

                {/* Adresse */}
                <FormGroup className="row">
                  <Label className="form-control-label" md="2">
                    Adresse
                  </Label>
                  <Col md="9">
                    <InputGroup className={classnames("input-group-merge")}>
                      <Input
                        placeholder="Adresse"
                        name="adresse"
                        type="text"
                        value={client.adresse}
                        onChange={onChange}
                      />
                    </InputGroup>
                  </Col>
                </FormGroup>

                {/* Country */}
                <FormGroup className="row">
                  <Label className="form-control-label" md="2">
                    Country
                  </Label>
                  <Col md="9">
                    <Select
                      value={updatedCountries.filter(
                        (opt) => opt.value === client.country_code
                      )}
                      name="country_code"
                      options={updatedCountries}
                      onChange={onChangeCountryCode}
                      className={!validation.country_code ? "is-invalid" : ""}
                    />
                    {!validation.country_code ? (
                      <InputGroup className="invalid-feedback">
                        Required Country.
                      </InputGroup>
                    ) : (
                      ""
                    )}
                  </Col>
                </FormGroup>

                <hr className="my-4" />
                <h6 className="heading-small text-muted mb-4">Contact</h6>

                {/* Website */}
                <FormGroup className="row">
                  <Label className="form-control-label" md="2">
                    Website
                  </Label>
                  <Col md="7">
                    <InputGroup className={classnames("input-group-merge")}>
                      <InputGroupText>
                        <i className="ni ni-world" />
                      </InputGroupText>
                      <Input
                        placeholder="https://www.example.com/.."
                        name="website"
                        type="text"
                        value={client.contacts.website}
                        onChange={onChange}
                      />
                    </InputGroup>
                  </Col>
                  <Col></Col>
                </FormGroup>

                {/* Email */}
                <FormGroup className="row">
                  <Label className="form-control-label" md="2">
                    Email
                  </Label>
                  <Col md="7">
                    <InputGroup className={classnames("input-group-merge")}>
                      <InputGroupText>
                        <i className="ni ni-email-83" />
                      </InputGroupText>
                      <Input
                        placeholder="example@example.com"
                        name="email"
                        type="text"
                        value={client.contacts.email}
                        onChange={onChange}
                      />
                    </InputGroup>
                  </Col>
                  <Col></Col>
                </FormGroup>

                {/* Number */}
                <FormGroup className="row">
                  <Label className="form-control-label" md="2">
                    Number
                  </Label>
                  <Col md="7">
                    <InputGroup className={classnames("input-group-merge")}>
                      <InputGroupText>
                        <i className="fa fa-phone" />
                      </InputGroupText>
                      <Input
                        placeholder="+ xx xx xx xx "
                        name="number"
                        type="tel"
                        value={client.contacts.number}
                        onChange={onChange}
                      />
                    </InputGroup>
                  </Col>
                  <Col></Col>
                </FormGroup>

                <hr className="my-4" />
                <h6 className="heading-small text-muted mb-4">Social Media</h6>

                {/* Linkedin */}
                <FormGroup className="row">
                  <Label className="form-control-label" md="2">
                    Linkedin
                  </Label>
                  <Col md="7">
                    <InputGroup className={classnames("input-group-merge")}>
                      <InputGroupText>
                        <i className="fab fa-linkedin" />
                      </InputGroupText>
                      <Input
                        placeholder="https://www.linkedin.com/company/.."
                        name="linkedin"
                        type="text"
                        value={client.social_links.linkedin}
                        onChange={onChange}
                      />
                    </InputGroup>
                  </Col>
                  <Col></Col>
                </FormGroup>

                {/* Facebook */}
                <FormGroup className="row">
                  <Label className="form-control-label" md="2">
                    Facebook
                  </Label>
                  <Col md="7">
                    <InputGroup className={classnames("input-group-merge")}>
                      <InputGroupText>
                        <i className="fab fa-facebook" />
                      </InputGroupText>
                      <Input
                        placeholder="https://www.facebook.com/.."
                        name="facebook"
                        type="text"
                        value={client.social_links.facebook}
                        onChange={onChange}
                      />
                    </InputGroup>
                  </Col>
                  <Col></Col>
                </FormGroup>

                {/* Twitter */}
                <FormGroup className="row">
                  <Label className="form-control-label" md="2">
                    Twitter
                  </Label>
                  <Col md="7">
                    <InputGroup className={classnames("input-group-merge")}>
                      <InputGroupText>
                        <i className="fab fa-twitter" />
                      </InputGroupText>
                      <Input
                        placeholder="https://twitter.com.."
                        name="twitter"
                        type="text"
                        value={client.social_links.twitter}
                        onChange={onChange}
                      />
                    </InputGroup>
                  </Col>
                  <Col></Col>
                </FormGroup>

                <hr className="my-4" />

                {/*
                  SCRUM-1818: a 409 from POST /api/clients/add names the
                  soft-deleted client that still holds this name. Two ways
                  out, each saying what happens to the old client's links.
                */}
                {deletedHolder && (
                  <div className="alert alert-warning" role="alert">
                    <p className="mb-2">
                      {deletedHolder.msg}
                      {deletedHolder.value ? (
                        <>
                          {" "}
                          (<strong>{deletedHolder.value}</strong>)
                        </>
                      ) : null}
                      .
                    </p>
                    <p className="mb-2">
                      <Button
                        type="button"
                        size="sm"
                        color="warning"
                        className="mr-2"
                        onClick={onRestore}
                      >
                        Restore
                      </Button>
                      Restore brings the old client back, with its products and
                      partner data. This new client is not created.
                    </p>
                    <p className="mb-0">
                      <Button
                        type="button"
                        size="sm"
                        outline
                        color="warning"
                        className="mr-2"
                        onClick={onStartFresh}
                      >
                        Start fresh
                      </Button>
                      Start fresh creates a new client. The old client&apos;s
                      links (products, partner data) stay with the deleted
                      record and do not move to the new one.
                    </p>
                  </div>
                )}

                <Button color="primary" onClick={onClickAdd} disabled={loading}>
                  {loading ? "Adding..." : "Add"}
                </Button>

                <Button
                  to={"/admin/client/all"}
                  tag={Link}
                  className="btn btn-default "
                >
                  All Clients
                </Button>
              </CardBody>
            </Card>
          </Col>

          {/* Right column: Upload */}
          <Col className="order-xl-2" xl="4">
            <Card className="card-pricing border-0 text-center mb-4">
              <CardHeader className="bg-gradient-success">
                <h4 className="text-uppercase ls-0 text-white py-2 mb-0">
                  Upload Logo
                </h4>
              </CardHeader>
              <CardBody className="px-lg-2">
                <ImageUploadContainer
                  ImageUpload={ImageUploadRecievedHandler}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default AddNewClientView;
